import React, { useEffect, useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { assetLifeCycleCostByPeriod } from '../../api/tasksAPI';
import { showToastError } from '../../utils/errorController';

const chartColor = '#0d6efd';

const formatNumber = (value) => {
  if (!Number.isFinite(value)) {
    return '0';
  }

  return value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatOptionalNumber = (value) => {
  if (!Number.isFinite(value)) {
    return '-';
  }

  return formatNumber(value);
};

const AssetLifeCycleCostTab = ({ assetId, requestKey }) => {
  const [lifeCycleData, setLifeCycleData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasRequested, setHasRequested] = useState(false);
  const [chartMode, setChartMode] = useState('costPerUnit');

  useEffect(() => {
    if (!assetId || requestKey === 0) {
      return;
    }

    let isMounted = true;
    setHasRequested(true);
    setLoading(true);

    assetLifeCycleCostByPeriod(assetId)
      .then((response) => {
        if (!isMounted) return;
        setLifeCycleData(response);
      })
      .catch((error) => {
        if (!isMounted) return;
        showToastError('Unable to load life cycle cost data.');
        console.error('Failed to load life cycle cost data', error);
      })
      .finally(() => {
        if (!isMounted) return;
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [assetId, requestKey]);

  const targetAsset = useMemo(() => {
    const assets = Array.isArray(lifeCycleData?.assets) ? lifeCycleData.assets : [];
    if (assets.length === 0) {
      return null;
    }

    const matched = assets.find((asset) => String(asset?.id) === String(assetId));
    return matched ?? assets[0] ?? null;
  }, [lifeCycleData, assetId]);

  const periodCosts = useMemo(
    () => (Array.isArray(targetAsset?.periodCosts) ? targetAsset.periodCosts : []),
    [targetAsset],
  );

  const summaryMetrics = useMemo(() => {
    if (!periodCosts.length) {
      return {
        totalCostPerUnit: null,
        partsCostPerUnit: null,
        labourCostPerUnit: null,
        servicesCostPerUnit: null,
        maintenanceRatio: null,
        meterUnits: 'unit',
      };
    }

    const lastEntry = periodCosts[periodCosts.length - 1] ?? {};
    const meter = targetAsset?.meters?.[0] ?? null;
    const usageDelta = (meter?.endUsage ?? 0) - (meter?.startUsage ?? 0);
    const totalCostPerUnit = lastEntry.totalCostPerHour ?? 0;
    const partsCostPerUnit = (lastEntry.cumulativePartsCost ?? 0) / usageDelta;
    const labourCostPerUnit = (lastEntry.cumulativeLabourCost ?? 0) / usageDelta;
    const servicesCostPerUnit = (lastEntry.cumulativeServiceCost ?? 0) / usageDelta;
    const meterUnits =
      typeof meter?.unit === 'string'
        ? meter.unit.toLowerCase().replace(/s$/, '')
        : 'unit';

    const maintenanceRatio =
      Number.isFinite(usageDelta) && usageDelta > 0
        ? (lastEntry.cumulativeLabourHours ?? 0) / usageDelta
        : null;

    return {
      totalCostPerUnit,
      partsCostPerUnit,
      labourCostPerUnit,
      servicesCostPerUnit,
      maintenanceRatio,
      meterUnits,
    };
  }, [periodCosts, targetAsset]);

  const series = useMemo(() => {
    if (!periodCosts.length) {
      return [];
    }

    const seriesName =
      chartMode === 'costPerUnit'
        ? `Cost per ${summaryMetrics.meterUnits.toLowerCase()}`
        : chartMode === 'componentValue'
          ? 'Component Value'
          : '';

    return [
      {
        name: seriesName,
        data:
          chartMode === 'costPerUnit'
            ? periodCosts.map((entry) => ({
              x: entry.usage ?? 0,
              y: entry.totalCostPerHour ?? 0,
            }))
            : chartMode === 'componentValue'
              ? periodCosts.map((entry) => ({
                x: entry.usage ?? 0,
                y: entry.componentValue ?? 0,
              }))
            : periodCosts.map((entry) =>
              chartMode === 'absoluteCost'
                ? entry.totalCost ?? 0
                : entry.cumulativeTotalCost ?? 0,
            ),
      },
    ];
  }, [chartMode, periodCosts, summaryMetrics.meterUnits]);

  const usageLabels = useMemo(
    () => periodCosts.map((entry) => entry.usage ?? 0),
    [periodCosts],
  );

  const meterUnitsLabel = summaryMetrics.meterUnits.toLowerCase();

  const options = useMemo(
    () => ({
      chart: {
        type: chartMode === 'costPerUnit' || chartMode === 'componentValue' ? 'line' : 'bar',
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      dataLabels: {
        enabled: false,
      },
      colors: [chartColor],
      stroke: { width: chartMode === 'costPerUnit' || chartMode === 'componentValue' ? 3 : 0 },
      markers: { size: chartMode === 'costPerUnit' || chartMode === 'componentValue' ? 4 : 0 },
      xaxis: {
        type: chartMode === 'costPerUnit' || chartMode === 'componentValue' ? 'numeric' : 'category',
        categories: chartMode === 'costPerUnit' || chartMode === 'componentValue' ? [] : usageLabels,
        tickAmount: chartMode === 'costPerUnit' || chartMode === 'componentValue'
          ? undefined
          : Math.ceil(usageLabels.length / 4),
        title: { text: 'Usage' },
      },
      yaxis: {
        title: {
          text: chartMode === 'componentValue' ? 'Component value' : `Cost per ${meterUnitsLabel}`,
        },
        labels: {
          formatter: (val) => formatNumber(val),
        },
      },
      tooltip: {
        y: {
          formatter: (val) => formatNumber(val),
        },
      },
      legend: {
        show: chartMode === 'costPerUnit' || chartMode === 'componentValue',
      },
      grid: { borderColor: '#e5e7eb' },
    }),
    [chartMode, meterUnitsLabel, usageLabels],
  );

  if (!hasRequested) {
    return (
      <div className="row g-3">
        <div className="col-12 text-muted">
          Select the tab to load life cycle cost data.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="row g-3">
        <div className="col-12 d-flex align-items-center gap-2">
          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
          <span>Loading life cycle cost data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="row g-3">
      <div className="col-12">
        <div className="row g-3">
          <div className="col-12 col-md-6 col-xl-4">
            <div className="border rounded p-3 h-100">
              <div className="text-muted small">Total life cycle cost per {summaryMetrics.meterUnits.toLowerCase()}</div>
              <div className="fw-semibold">{formatOptionalNumber(summaryMetrics.totalCostPerUnit)}</div>
            </div>
          </div>
          <div className="col-12 col-md-6 col-xl-4">
            <div className="border rounded p-3 h-100">
              <div className="text-muted small">Maintenance ratio</div>
              <div className="fw-semibold">{formatOptionalNumber(summaryMetrics.maintenanceRatio)}</div>
            </div>
          </div>
          <div className="col-12 col-md-6 col-xl-4">
            <label className="form-label">Chart view</label>
            <select
              className="form-select"
              value={chartMode}
              onChange={(event) => setChartMode(event.target.value)}
            >
              <option value="costPerUnit">Cost per {meterUnitsLabel}</option>
              <option value="componentValue">Component Value</option>
              <option value="absoluteCost">Absolute cost per period</option>
              <option value="cumulativeCost">Cumulative cost per period</option>
            </select>
          </div>
          <div className="col-12 col-md-6 col-xl-4">
            <div className="border rounded p-3 h-100">
              <div className="text-muted small">Parts life cycle cost per {summaryMetrics.meterUnits.toLowerCase()}</div>
              <div className="fw-semibold">{formatOptionalNumber(summaryMetrics.partsCostPerUnit)}</div>
            </div>
          </div>
          <div className="col-12 col-md-6 col-xl-4">
            <div className="border rounded p-3 h-100">
              <div className="text-muted small">Labour life cycle cost per {summaryMetrics.meterUnits.toLowerCase()}</div>
              <div className="fw-semibold">{formatOptionalNumber(summaryMetrics.labourCostPerUnit)}</div>
            </div>
          </div>
          <div className="col-12 col-md-6 col-xl-4">
            <div className="border rounded p-3 h-100">
              <div className="text-muted small">Services life cycle cost per {summaryMetrics.meterUnits.toLowerCase()}</div>
              <div className="fw-semibold">{formatOptionalNumber(summaryMetrics.servicesCostPerUnit)}</div>
            </div>
          </div>
        </div>
      </div>
      <div className="col-12">
        {series.length > 0 ? (
          <ReactApexChart
            key={chartMode}
            options={options}
            series={series}
            type={chartMode === 'costPerUnit' || chartMode === 'componentValue' ? 'line' : 'bar'}
            height={500}
          />
        ) : (
          <div className="text-muted">No life cycle cost data available for this asset.</div>
        )}
      </div>
    </div>
  );
};

export default AssetLifeCycleCostTab;
